import SignInLayout from 'src/layouts/signin-layout'
import FormItem from 'src/components/atoms/form-item'
import FullScreenMessage from 'src/components/molecules/full-screen-message'

import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { LockOutlined } from '@ant-design/icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ResetPassSchema } from '../password-recovery/schema'
import { Button, Col, Flex, Form, Input, Row, Typography } from 'antd'

const TIME_TO_REDIRECT = 5000

type ResetPassType = {
  password: string
  passwordConfirmation: string
}

export default function NewPasswordForm() {
  const params = useParams()
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const [error, setError] = useState('')
  const [showSuccessMsg, setShowSuccessMsg] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPassType>({
    resolver: yupResolver(ResetPassSchema),
    defaultValues: { password: '', passwordConfirmation: '' },
  })

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null

    if (showSuccessMsg) {
      timeout = setTimeout(() => {
        navigate('/dashboard')
      }, TIME_TO_REDIRECT)
    }

    if (error) {
      timeout = setTimeout(() => {
        setError('')
      }, TIME_TO_REDIRECT)
    }

    if (timeout) {
      return () => {
        if (timeout) clearTimeout(timeout)
      }
    }
  }, [showSuccessMsg, error, navigate])

  const handleFinish = (formValues: ResetPassType) => {
    const payload = {
      code: params?.code,
      ...formValues,
    }

    try {
      //request - update user password
      console.log(payload)
      setShowSuccessMsg(true)
    } catch (err) {
      setError(i18n.t('errors.unspecific'))
    }
  }

  return error ? (
    <FullScreenMessage
      type="error"
      title={i18n.t('newPassword.error')}
      content={error}
    />
  ) : showSuccessMsg ? (
    <FullScreenMessage
      type="success"
      title={i18n.t('newPassword.success')}
      content={i18n.t('newPassword.successDescription')}
    />
  ) : (
    <SignInLayout>
      <Form
        layout="vertical"
        wrapperCol={{ span: 24 }}
        onFinish={handleSubmit(handleFinish)}
        className="md:w-50vw w-full max-w-[610px] p-4"
      >
        <Flex vertical align="start" gap="large" className="w-full">
          <Flex vertical align="middle" gap={40} className="w-full">
            <div className="w-full">
              <Typography.Title level={2} type="warning">
                {i18n.t('newPassword.title')}
              </Typography.Title>
              <Typography.Text>
                <Trans
                  i18n={i18n}
                  i18nKey="newPassword.description"
                  components={[<strong />]}
                />
              </Typography.Text>
            </div>
            <div className="w-full">
              <FormItem
                name="password"
                label={i18n.t('newPassword.password')}
                errors={errors}
                control={control}
                component={
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined />}
                    aria-invalid={Boolean(errors.password)}
                    data-testid="new-pass-input-password"
                    placeholder={i18n.t('newPassword.passwordPlaceholder')}
                  />
                }
              />
              <FormItem
                name="passwordConfirmation"
                label={i18n.t('newPassword.passwordConfirmation')}
                errors={errors}
                control={control}
                component={
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined />}
                    aria-invalid={Boolean(errors.passwordConfirmation)}
                    data-testid="new-pass-input-password-confirmation"
                    placeholder={i18n.t(
                      'newPassword.passwordConfirmationPlaceholder'
                    )}
                  />
                }
              />
            </div>
          </Flex>

          <Row className="w-full">
            <Col span={24}>
              <Button
                size="large"
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                className="w-full font-bold"
                data-testid="new-pass-submit-btn"
              >
                {i18n.t('newPassword.submit')}
              </Button>
            </Col>
          </Row>
        </Flex>
      </Form>
    </SignInLayout>
  )
}
